/**
 * payslipBranding — the letterhead that goes on every payslip, and the rules
 * about what may be stored as one.
 *
 * WHY THE LOGO IS DATA AND NOT AN ASSET. Shipping the mark as a file in
 * `public/` would make changing it a deploy, and a deploy here is a push to
 * `main` that goes straight to production. A logo is exactly the kind of
 * thing that gets replaced on a Friday by somebody who does not deploy, so it
 * lives in the module's own settings list and is uploaded from Payroll
 * Settings. `payslipDoc` takes it as an `<img src>` and prints without one
 * quite happily — a payslip with no letterhead is still a valid payslip.
 *
 * THE BUDGET IS THE STORAGE, NOT AN OPINION. SharePoint's plain-text
 * multi-line column holds 63,999 characters, so a data URI has to come in
 * under that with room to spare or the write fails at save time — long after
 * the person picked the file. `LOGO_MAX_CHARS` is that ceiling with a margin,
 * and `logoEncodeSteps` is the ladder of (size, format, quality) the uploader
 * walks down until the encoding fits: PNG first, because a letterhead wants a
 * transparent background, then JPEG on white when PNG cannot get small
 * enough, then smaller, and only then a refusal that says what to do.
 *
 * Pure and I/O-free — the canvas work lives on the screen that owns a DOM.
 */

import { asDescDesign, type DescDesign } from "./garmentName";

/** The settings key the letterhead is stored under. */
export const PAYSLIP_LOGO_KEY = "payslipLogo";

/**
 * Characters a stored data URI may occupy. SharePoint's limit is 63,999; the
 * margin covers the URI prefix and any encoding SharePoint applies on the way
 * in, because being 200 characters over is the same failure as being 20,000
 * over and both happen at save time.
 */
export const LOGO_MAX_CHARS = 58_000;

/**
 * How tall the mark prints on the payslip masthead, in CSS pixels — a
 * CEILING, with a width ceiling beside it, not a fixed size.
 *
 * A letterhead that stacks a wordmark over a tagline is close to square, and
 * a squat mark scaled to a fixed height comes out narrow enough that the
 * tagline is a grey smear on paper. A wide one scaled to the same height runs
 * halfway across the masthead instead. Bounding both edges lets each shape
 * take the space it needs and no more.
 *
 * Encoding at several times this (see `logoEncodeSteps`) keeps it crisp on a
 * 2x print without paying for a photograph.
 */
export const LOGO_PRINT_HEIGHT = 72;
export const LOGO_PRINT_MAX_WIDTH = 190;

export interface ILogoStep {
  /** Longest edge of the encoded bitmap, in pixels. */
  maxEdge: number;
  type: "image/png" | "image/jpeg";
  /** JPEG quality; ignored for PNG. */
  quality: number;
}

/**
 * The ladder the uploader walks down until an encoding fits the budget.
 *
 * PNG at 2x print size first — that is what a clean logo should be, and it
 * keeps the transparent background. JPEG on white is the fallback for a mark
 * that was handed over as a photograph or a screenshot, where PNG would be
 * hundreds of kilobytes of noise.
 */
export function logoEncodeSteps(): ILogoStep[] {
  return [
    { maxEdge: 480, type: "image/png", quality: 1 },
    { maxEdge: 360, type: "image/png", quality: 1 },
    { maxEdge: 480, type: "image/jpeg", quality: 0.9 },
    { maxEdge: 360, type: "image/jpeg", quality: 0.85 },
    { maxEdge: 260, type: "image/jpeg", quality: 0.8 },
  ];
}

/** Fit `w x h` inside a square of `maxEdge` without enlarging it — a 200px
 *  logo uploaded as-is should not be blown up to 480 and blurred. */
export function fitWithin(w: number, h: number, maxEdge: number): { w: number; h: number } {
  const longest = Math.max(w, h);
  if (longest <= 0) return { w: 0, h: 0 };
  const scale = Math.min(1, maxEdge / longest);
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}

/** Whether a string is something we would put in an `<img src>` on a document
 *  somebody signs. Data URIs only: an http(s) URL would make the payslip
 *  depend on a host being up at print time, and a `javascript:` or `blob:`
 *  src has no business on a printable form. */
export function isStorableLogo(v: string | null | undefined): boolean {
  const s = String(v ?? "").trim();
  return /^data:image\/(png|jpeg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/.test(s)
    && s.length <= LOGO_MAX_CHARS;
}

/** Roughly what a data URI weighs on disk, for the "12 KB" the screen shows.
 *  Base64 is 4 characters per 3 bytes, and the prefix is not payload. */
export function logoBytes(dataUrl: string): number {
  const i = dataUrl.indexOf(",");
  if (i < 0) return 0;
  const b64 = dataUrl.slice(i + 1);
  const pad = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - pad);
}

// ─── Document control ────────────────────────────────────────────────────────

/** The settings key the form-control block is stored under. */
export const PAYSLIP_FORM_KEY = "payslipForm";

/**
 * The controlled-document identity a payslip prints in its masthead —
 * form code, revision and effective date, the three things an ISO 9001
 * auditor reads off a form before reading the form.
 *
 * DATA, NOT CODE, for the same reason the letterhead is: the QMS owner bumps
 * a revision when the layout changes, and that person does not deploy. The
 * defaults below are what a payslip prints until somebody sets them on
 * Payroll Settings; the effective date is the day this block first shipped.
 */
export interface IPayslipForm {
  /** e.g. "IGO-PAY-F01". */
  code: string;
  /** e.g. "01". Printed as "Rev. 01". */
  revision: string;
  /** ISO day the revision took effect. */
  effective: string;
  /**
   * Which DESCRIPTION design the work table prints BY DEFAULT — two choices
   * crossed, so four numbers: colour NAMES ("Black XXS") or SKU CODES
   * ("BLK-XXS"), each with or without the design label on every segment.
   * Mikko wanted them available (2026-09-22) and the choice is a SETTING
   * rather than a deploy, for the same reason the letterhead is: the person
   * who wants the other one is not the person who pushes to main. Every print
   * screen can override it for one session. See garmentName.DESC_DESIGNS.
   */
  descDesign: DescDesign;
}

export const DEFAULT_PAYSLIP_FORM: IPayslipForm = {
  code: "IGO-PAY-F01",
  revision: "01",
  effective: "2026-09-11",
  descDesign: 3,
};

/** Parse the stored block, field by field, defaults filling any gap. */
export function parseStoredForm(text: string | null | undefined): IPayslipForm {
  if (!text) return { ...DEFAULT_PAYSLIP_FORM };
  try {
    const v: unknown = JSON.parse(text);
    if (!v || typeof v !== "object" || Array.isArray(v)) return { ...DEFAULT_PAYSLIP_FORM };
    const r = v as Record<string, unknown>;
    const str = (k: "code" | "revision" | "effective") =>
      (typeof r[k] === "string" && (r[k] as string).trim() ? (r[k] as string).trim() : DEFAULT_PAYSLIP_FORM[k]);
    // Anything outside the four is Design 1 — a stored value nobody
    // recognises should print the default, never nothing.
    const descDesign = r.descDesign === undefined || r.descDesign === null ? DEFAULT_PAYSLIP_FORM.descDesign : asDescDesign(r.descDesign);
    return { code: str("code"), revision: str("revision"), effective: str("effective"), descDesign };
  } catch {
    return { ...DEFAULT_PAYSLIP_FORM };
  }
}
